/**
 * # AsText Component
 * 
 * A localization component that displays text based on a provided key. This component
 * integrates with a localization system to automatically display translated text
 * and update when language changes occur.
 * 
 * ## Features
 * - Displays localized text based on a key
 * - Automatically updates when language changes
 * - Integrates with the application's localization system
 * - Supports dynamic text updates via events
 * 
 * ## Component Hierarchy
 * ```
 * AsText (as-text)
 * └── Text content (innerText) - The localized text content
 * ```
 * 
 * ## Attributes
 * - `dict-key`: The key used to look up the localized text string
 * 
 * ## Events
 * ### Emitted Events
 * - `as-local-text-subscribe`: Dispatched when the component is connected to the DOM
 *   - Detail: `{ textKey: string, requester: AsText }` - The text key and a reference to this component
 * 
 * ### Handled Events
 * - `as-local-text-update`: Expected to be dispatched to this component when text needs to be updated
 *   - Detail: `{ text: string }` - The new text to display
 * - `as-register-text-elements`: Triggers the component to re-register itself with the localization system
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage
 * ```html
 * <as-text dict-key="common.ok">OK</as-text>
 * ```
 * 
 * ### With Default Text
 * The content between the tags is displayed until the localized text is loaded:
 * ```html
 * <as-text dict-key="navigation.home">Home</as-text>
 * ```
 * 
 * ### Updating Text Programmatically
 * ```javascript
 * // Get a reference to the as-text element
 * const textElement = document.querySelector('as-text[dict-key="user.greeting"]');
 * 
 * // Update its text
 * textElement.dispatchEvent(new CustomEvent('as-local-text-update', {
 *   detail: { text: 'Hello, User!' }
 * }));
 * ```
 * 
 * ### Implementing the Localization System
 * ```javascript
 * // Example of handling the subscription event in a parent component
 * document.addEventListener('as-local-text-subscribe', (e) => {
 *   const { textKey, requester } = e.detail;
 *   
 *   // Get the localized text from your system
 *   const localizedText = getLocalizedText(textKey);
 *   
 *   // Update the requesting component
 *   requester.dispatchEvent(new CustomEvent('as-local-text-update', {
 *     detail: { text: localizedText }
 *   }));
 *   
 *   // Set up a listener for language changes
 *   onLanguageChange((newLanguage) => {
 *     const updatedText = getLocalizedText(textKey, newLanguage);
 *     requester.dispatchEvent(new CustomEvent('as-local-text-update', {
 *       detail: { text: updatedText }
 *     }));
 *   });
 * });
 * ```
 * 
 * ## Implementation Notes
 * - The component does not handle the actual translation; it relies on a parent component or service
 * - Initial text content can be provided between the tags as a fallback
 * - Uses event-based communication for localization updates
 * - The component automatically subscribes to text updates when connected to the DOM
 */

export class AsText extends HTMLElement {
  static get observedAttributes() {
    return ['dict-key', 'default-text', 'vars'];
  }
  private appComponent: any = null;
  private pendingRegistration: boolean = false;
  private currentTextKey: string | null = null; // Track current key
  private _vars: any[] | null = null;

  get vars(): any[] | null {
    return this._vars;
  }

  set vars(value: any[] | null) {
    this._vars = value;
    if (this.currentTextKey) {
      this.registerTextKey();
    }
  }

  // Define the update handler that will be used for event listeners
  private updateHandler = (event: CustomEvent) => {
    this.innerText = event.detail.text;
  };
  private registerHandler = () => {
    this.registerTextKey();
  };
  
  constructor() {
    super();

  }
  
  // Request initial text and subscribe when connected to DOM
  connectedCallback() {
    // Handle property shadowing for vars
    if (this.hasOwnProperty('vars')) {
      const value = (this as any).vars;
      delete (this as any).vars;
      this.vars = value;
    }

    const defaultText = this.getAttribute('default-text');
    if (defaultText && !this.innerText) this.innerText = defaultText;
    this.addEventListener('as-local-text-update', this.updateHandler);
    
    // Listen for app component response both on self and window
    this.addEventListener('as-app', this.handleAppFound);
    window.addEventListener('as-app', this.handleAppFound);
    
    // Request the app component
    this.requestAppComponent();
  }
  
  // Handle receiving the app component reference
  private handleAppFound = (event: CustomEvent) => {
    const dictKey = this.getAttribute('dict-key');
    this.appComponent = event.detail.sender;
    
    // If we have a pending registration, register now
    if (this.pendingRegistration) {
      this.registerTextKey();
      this.pendingRegistration = false;
    }
  }
  
  // Request the app component
  private requestAppComponent() {
    const dictKey = this.getAttribute('dict-key');
    this.pendingRegistration = true;
    
    this.dispatchEvent(new CustomEvent('as-get-app', {
      bubbles: true,
      composed: true,
      detail: { requester: this }
    }));
  }

  // Register the text key with the localization system
  private registerTextKey() {
    const textKey = this.getAttribute('dict-key');
    if (!textKey) {
      // console.warn(this, "an as-text component has no dict-key attribute");
      return;
    }
    
    // If we don't have the app component yet, set pending and wait
    if (!this.appComponent) {
      this.pendingRegistration = true;
      return;
    }
    
    // Update current key tracking
    this.currentTextKey = textKey;
    
    // Dispatch subscription directly on the app component
    this.appComponent.dispatchEvent(new CustomEvent('as-local-text-subscribe', {
      detail: {
        textKey,
        requester: this,
        vars: this._vars
      }
    }));
  }

  // Unsubscribe from a text key
  private unsubscribeFromKey(textKey: string) {
    if (!textKey || !this.appComponent) {
      return;
    }
    
    this.appComponent.dispatchEvent(new CustomEvent('as-local-text-unsubscribe', {
      detail: {
        textKey,
        requester: this
      }
    }));
  }

  // Handle attribute changes to update text key subscription if needed
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;
    
    if (name === 'vars') {
      try {
        this.vars = newValue ? JSON.parse(newValue) : null;
      } catch (e) {
        console.error("as-text: failed to parse vars attribute", e);
      }
      return;
    }

    if (name === 'dict-key') {
      // Unsubscribe from old key if it exists
      if (oldValue && this.appComponent) {
        this.unsubscribeFromKey(oldValue);
      }

      // Subscribe to new key if it exists
      if (newValue) {
        this.registerTextKey();
      } else {
        this.currentTextKey = null;
      }
    }
    if (name === 'default-text') {
      if (!this.innerText) this.innerText = newValue;
    }
  }

  // Clean up event listeners when the component is removed
  disconnectedCallback() {
    const dictKey = this.getAttribute('dict-key');
    
    // Unsubscribe from current key
    if (this.currentTextKey && this.appComponent) {
      this.unsubscribeFromKey(this.currentTextKey);
    }
    
    if (this.updateHandler) {
      this.removeEventListener('as-local-text-update', this.updateHandler);
      this.updateHandler = null;
    }
    
    if (this.registerHandler) {
      document.removeEventListener('as-register-text-elements', this.registerHandler);
      this.registerHandler = null;
    }
    
    this.removeEventListener('as-app', this.handleAppFound);
    window.removeEventListener('as-app', this.handleAppFound);
    this.appComponent = null;
    this.currentTextKey = null;
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-text')) {
  customElements.define('as-text', AsText);
} else {
  console.warn("as-text already defined");
}
